import { ELSCommonUIConstants } from '@els/els-ui-common-react';
import {
  isArray,
  isNil,
  isObject
} from 'lodash';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { RecordObject } from '../models/common.models';
import { FlashcardDto, GeneratedQuizQuestionDto } from '../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';

export const isInstructor = (userRole: string): boolean => userRole === ELSCommonUIConstants.userType.Instructor;

export const isStudent = (userRole: string): boolean => userRole === ELSCommonUIConstants.userType.Student;

export const flattenTree = <T extends RecordObject>(array: T[], nestProp: string, level = 1): T[] => {
  return array.reduce((acc: T[], cur: T) => {
    const nested = cur[nestProp] as unknown;
    if (Array.isArray(nested) && nested.length) {
      return [...acc, cur, ...flattenTree(nested as T[], nestProp, level + 1)];
    }
    return [...acc, cur];
  }, [] as T[]);
};

export const stripNilProps = (obj: RecordObject): RecordObject => {
  const copy: RecordObject = {
    ...obj
  };
  Object.keys(copy).forEach((key) => {
    if (isNil(copy[key])) {
      delete copy[key];
    }
  });
  return copy;
};

// Recursively remove "empty" properties from an object: null/undefined and
// blank/whitespace-only strings (strings are trimmed before the check). Recurses
// into nested plain objects, and drops a nested object that becomes empty. Arrays
// and non-object values are kept as-is. Returns a new object; the input is not mutated.
export const stripEmptyProps = <T extends RecordObject>(obj: T): Partial<T> => {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];

    if (isNil(value)) {
      return acc;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? acc : { ...acc, [key]: trimmed };
    }

    if (isObject(value) && !isArray(value)) {
      const nested = stripEmptyProps(value as RecordObject);
      return Object.keys(nested).length ? { ...acc, [key]: nested } : acc;
    }

    return { ...acc, [key]: value };
  }, {} as Partial<T>);
};

export const isOnlyNumber = (str: string): boolean => {
  const pattern = /^[0-9]*$/gm;
  return pattern.test(str);
};

export const convertStringForClass = (string: string): string => {
  return string
    .toLowerCase()
  .replace(/ /g, '-')
  .replace(/\/|\(|\)/g, '');
};

export const mapToIds = (x: { id: string | number }): string | number => x.id;

export const joinListCommaSeparated = (items: string[]): string => {
  if (!items || !items.length) {
    return '';
  }
  if (items.length === 1) {
    return items[0];
  }
  return items.reduce((acc, cur, idx, arr) => {
    if (idx === 0) {
      return cur;
    }
    if (idx + 1 === arr.length) {
      return `${acc} & ${cur}`;
    }
    return `${acc}, ${cur}`;
  });
};

export const stitchArrays = <T>(arrays: T[][]): T[] => {
  const lengths = arrays.map(a => a.length);
  if (lengths.every(l => l === 0)) {
    return [];
  }
  const indices = arrays.map(() => 0);
  const output: T[] = [];
  let remaining = arrays.reduce((acc, a) => acc + a.length, 0);
  let currentIndex = 0;

  while (remaining > 0) {
    const arr = arrays[currentIndex];
    const idx = indices[currentIndex];
    if (idx < arr.length) {
      output.push(arr[idx]);
      indices[currentIndex] += 1;
      remaining -= 1;
    }
    currentIndex = (currentIndex + 1) % arrays.length;
  }
  return output;
};

export const truncateTitle = (title: string, maxLength: number): string => {
  if (isNil(maxLength) || !title || !title.length || title.length <= maxLength) {
    return title;
  }
  return `${title.substring(0, maxLength - 3)}...`;
};

export const getObjectPropsArray = (propVal: unknown, acc: { prop: string; val: unknown }[] = [], path = ''): {
  prop: string;
  val: unknown;
}[] => {

  if (isArray(propVal)) {
    return propVal.reduce((_acc, cur, idx) => {
      return getObjectPropsArray(cur, _acc, path ? `${path}[${idx}]` : `[${idx}]`);
    }, acc);
  }

  if (!isObject(propVal)) {
    return [
      ...acc,
      {
        prop: path,
        val: propVal
      }
    ];
  }

  return Object.keys(propVal).reduce((_acc, prop) => {

    if (isArray(propVal[prop]) || isObject(propVal[prop])) {
      return getObjectPropsArray(propVal[prop], _acc, path ? `${path}.${prop}` : prop);
    }

    return [
      ..._acc,
      {
        prop: path ? `${path}.${prop}` : prop,
        val: propVal[prop]
      }
    ];
  }, acc);
};

export const filterBySearchQuery = (searchQuery: string, wordsToSearch: string[]): boolean => {
  if (!searchQuery || !searchQuery.length) {
    return true;
  }
  if (!wordsToSearch || !wordsToSearch.length) {
    return false;
  }
  const searchQueryWordList = searchQuery.toLowerCase().split(' ').map(item => item.trim());
  if (!searchQueryWordList || !searchQueryWordList.length) {
    return true;
  }
  const wordsToSearchNormalized = wordsToSearch.map(item => item.toLowerCase().trim());
  return searchQueryWordList.every(searchWord => {
    if (searchWord === '') {
      return true;
    }
    return wordsToSearchNormalized.some(word => {
      return word.includes(searchWord);
    });
  });
};

export const isTrueValue = (val: string): boolean => {
  if (isNil(val)) {
    return false;
  }
  return val.toString().toLowerCase() === 'true';
};

export const copyContentToClipboard = async (content: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(content);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to copy content to clipboard:', error);
  }
};

const BLOCK_LEVEL_TAGS = [
  'P', 'LI', 'BR', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TR',
  'BLOCKQUOTE', 'PRE', 'OL', 'UL', 'TABLE', 'HR'
];

// Table cells sit on the same row, so they are separated inline rather than by a line break.
const CELL_LEVEL_TAGS = ['TD', 'TH'];

// Shared with any component that renders markdown on-screen (e.g. FlashcardPlayer, AiChatEntry),
// so copy-to-clipboard plain text conversion always matches what's visually rendered.
export const MARKDOWN_REMARK_PLUGINS = [remarkGfm];
export const MARKDOWN_REHYPE_PLUGINS = [rehypeRaw];

// Convert a markdown string (which may contain embedded raw HTML) to plain text by rendering it
// through the same markdown pipeline used for on-screen display and then extracting the text,
// keeping line breaks between block-level elements.
// Browser-only: relies on the DOMParser global to walk the rendered markup.
export const convertMarkdownToPlainText = (markdown: string | null | undefined): string => {
  if (!markdown) {
    return '';
  }

  try {
    const html = ReactDOMServer.renderToStaticMarkup(
      React.createElement(
        ReactMarkdown as React.ComponentType<Record<string, unknown>>,
        {
          remarkPlugins: MARKDOWN_REMARK_PLUGINS,
          rehypePlugins: MARKDOWN_REHYPE_PLUGINS
        },
        markdown
      )
    );

    const doc = new DOMParser().parseFromString(html, 'text/html');

    const walk = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
      }

      const childText = Array.from(node.childNodes).map(walk).join('');

      if (node.nodeType === Node.ELEMENT_NODE) {
        const { tagName } = node as Element;
        if (BLOCK_LEVEL_TAGS.includes(tagName)) {
          return `${childText}\n`;
        }
        if (CELL_LEVEL_TAGS.includes(tagName)) {
          return `${childText}\t`;
        }
      }

      return childText;
    };

    return walk(doc.body)
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n')
      .trim();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to convert markdown to plain text:', error);
    return markdown;
  }
};

export const formatFlashcardsForCopy = (flashcards: FlashcardDto[]): string => {
  return flashcards
    .map((card, index) => {
      const front = convertMarkdownToPlainText(card.frontText);
      const back = convertMarkdownToPlainText(card.backText);
      return `Flashcard ${index + 1}:\nFront: ${front}\nBack: ${back}`;
    })
    .join('\n\n');
};

export const formatQuizQuestionsForCopy = (questions: GeneratedQuizQuestionDto[]): string => {
  return [...questions]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((question, index) => {
      const qtiData = question.questionData?.qtiData;
      const responseChoices = qtiData?.responseChoices ?? {};
      const feedback = qtiData?.feedback ?? {};
      const correctResponse = qtiData?.correctResponse ?? [];
      const choiceIds = Object.keys(responseChoices).sort();

      const choicesText = choiceIds
        .map((id, choiceIndex) => {
          const choiceText = `${String.fromCharCode(65 + choiceIndex)}. ${convertMarkdownToPlainText(responseChoices[id])}`;
          return correctResponse.includes(id) ? `${choiceText} (correct answer)` : choiceText;
        })
        .join('\n');
      // Named separately from the inline marker so a pasted question still has
      // one greppable line, and so multiple correct choices read as a set.
      const correctAnswerLetters = choiceIds
        .filter((id) => correctResponse.includes(id))
        .map((id) => String.fromCharCode(65 + choiceIds.indexOf(id)))
        .join(', ');
      const rationaleText = choiceIds
        .map((id, choiceIndex) => (feedback[id]
          ? `${String.fromCharCode(65 + choiceIndex)}. ${convertMarkdownToPlainText(feedback[id])}`
          : null))
        .filter(Boolean)
        .join('\n');

      const promptText = convertMarkdownToPlainText(qtiData?.prompt ?? '');

      const correctAnswerText = correctAnswerLetters
        ? `\n\nCorrect answer: ${correctAnswerLetters}`
        : '';

      return `Question ${index + 1}: ${promptText}\n\nChoices:\n${choicesText}${correctAnswerText}\n\nRationale:\n${rationaleText}`;
    })
    .join('\n\n');
};
